import type { NestedObject } from '@gemini/lux-ds-pms';
import type { FormBuilderObOptions } from '@components/FormBuilder/FormBuilder.ob';
import { FormIds } from '@utils/enums/FormIds.enum';
import type { Patient } from '@store/patientStore';
import { StaticValues } from '@utils/enums/StaticValues.enum';
import type { FieldStatus } from '@utils/interfaces';

export const shouldShowPrimaryInsurancePlanQuestion = (
  showInsuranceInPatientDetails: boolean,
  primaryInsurancePlanStatus?: FieldStatus,
) =>
  showInsuranceInPatientDetails &&
  primaryInsurancePlanStatus !== StaticValues.FIELD_STATUS_HIDDEN;

export const isPrimaryInsurancePlanRequired = (
  primaryInsurancePlanStatus?: FieldStatus,
) => primaryInsurancePlanStatus !== StaticValues.FIELD_STATUS_OPTIONAL;

export const shouldShowSecondaryInsurancePlanQuestion = (
  secondaryInsurancePlanStatus?: FieldStatus,
) => secondaryInsurancePlanStatus !== StaticValues.FIELD_STATUS_HIDDEN;

export const isSecondaryInsurancePlanRequired = (
  secondaryInsurancePlanStatus?: FieldStatus,
) => secondaryInsurancePlanStatus !== StaticValues.FIELD_STATUS_OPTIONAL;

export interface PatientDetailsInsuranceRules {
  askForInsuranceInformation: boolean;
  insuranceDrivesAvailability: boolean;
  primaryInsurancePlanRequired: boolean;
  secondaryInsurancePlanRequired: boolean;
  showCustomInsuranceFields: boolean;
  showPrimaryInsurancePlanQuestion: boolean;
  showSecondaryInsurancePlanQuestion: boolean;
}

export interface PatientDetailsValidationLists {
  customInsuranceFieldsValid: boolean[];
  insurancePlan: string[];
  listEnableButton: Array<boolean | undefined>;
  primaryInsuranceValid: boolean[];
  secondaryInsuranceAnswer: string[];
  secondaryInsuranceValid: boolean[];
}

export interface CurrentPatientValidationState {
  currentSecondaryInsuranceFormVisible: boolean;
}

export const getSecondaryInsurancePlanAnswer = (patient?: Patient) =>
  patient?.patientDetails?.secondaryInsuranceDetails?.submitInsurancePlan ||
  (patient?.insurance?.secondaryInsurance?.id ? StaticValues.YES : '');

export const getPatientDisplayName = (
  patient: Patient | undefined,
  fallbackName: string,
) => {
  const firstName = patient?.patientDetails?.firstName?.trim();
  const lastName = patient?.patientDetails?.lastName?.trim();

  return firstName || lastName
    ? [firstName, lastName].filter(Boolean).join(' ')
    : fallbackName;
};

export const shouldShowInsuranceSection = ({
  patient,
  rules,
}: {
  patient?: Patient;
  rules: Pick<
    PatientDetailsInsuranceRules,
    | 'askForInsuranceInformation'
    | 'insuranceDrivesAvailability'
    | 'showPrimaryInsurancePlanQuestion'
  >;
}) => {
  if (!rules.askForInsuranceInformation) {
    return false;
  }

  if (!rules.insuranceDrivesAvailability) {
    return (
      rules.showPrimaryInsurancePlanQuestion ||
      patient?.insurance?.plan === StaticValues.YES
    );
  }

  return patient?.insurance?.plan === StaticValues.YES;
};

export const shouldShowInsuranceForms = ({
  patient,
  index,
  insurancePlan,
  insuranceDrivesAvailability,
}: {
  patient: Patient;
  index: number;
  insurancePlan: string[];
  insuranceDrivesAvailability: boolean;
}) => {
  if (insuranceDrivesAvailability) {
    return patient.insurance?.plan === StaticValues.YES;
  }

  return (insurancePlan[index] || patient.insurance?.plan) === StaticValues.YES;
};

export const shouldShowSecondaryInsuranceForm = ({
  patient,
  index,
  secondaryInsuranceAnswer,
}: {
  patient: Patient;
  index: number;
  secondaryInsuranceAnswer: string[];
}) =>
  (secondaryInsuranceAnswer[index] ||
    getSecondaryInsurancePlanAnswer(patient)) === StaticValues.YES;

export const isPatientInsuranceIncomplete = ({
  patient,
  index,
  rules,
  validationLists,
}: {
  patient: Patient;
  index: number;
  rules: PatientDetailsInsuranceRules;
  validationLists: PatientDetailsValidationLists;
}) => {
  if (!shouldShowInsuranceSection({ patient, rules })) {
    return false;
  }

  const currentInsurancePlan =
    validationLists.insurancePlan[index] || patient.insurance?.plan;

  if (
    rules.showPrimaryInsurancePlanQuestion &&
    rules.primaryInsurancePlanRequired &&
    !currentInsurancePlan
  ) {
    return true;
  }

  if (
    !shouldShowInsuranceForms({
      patient,
      index,
      insurancePlan: validationLists.insurancePlan,
      insuranceDrivesAvailability: rules.insuranceDrivesAvailability,
    })
  ) {
    return false;
  }

  if (validationLists.primaryInsuranceValid[index] !== true) {
    return true;
  }

  if (
    rules.showCustomInsuranceFields &&
    validationLists.customInsuranceFieldsValid[index] !== true
  ) {
    return true;
  }

  if (!rules.showSecondaryInsurancePlanQuestion) {
    return false;
  }

  const currentSecondaryInsuranceAnswer =
    validationLists.secondaryInsuranceAnswer[index] ||
    getSecondaryInsurancePlanAnswer(patient);

  if (!currentSecondaryInsuranceAnswer) {
    return rules.secondaryInsurancePlanRequired;
  }

  if (currentSecondaryInsuranceAnswer === StaticValues.NO) {
    return false;
  }

  return validationLists.secondaryInsuranceValid[index] !== true;
};

export const isPatientDetailsStepComplete = ({
  patients,
  rules,
  validationLists,
}: {
  patients: Array<Patient | undefined>;
  rules: PatientDetailsInsuranceRules;
  validationLists: PatientDetailsValidationLists;
}) => {
  const patientDetailsAreComplete = validationLists.listEnableButton.every(
    enable => (typeof enable === 'boolean' && enable) || enable === undefined,
  );
  const insuranceSectionsAreComplete = patients.every((patient, index) => {
    if (!patient) {
      return true;
    }

    return !isPatientInsuranceIncomplete({
      patient,
      index,
      rules,
      validationLists,
    });
  });

  return patientDetailsAreComplete && insuranceSectionsAreComplete;
};

export const getCurrentPatientValidationState = ({
  currentPatientIndex,
  patients,
  rules,
  validationLists,
}: {
  currentPatientIndex: number;
  patients: Array<Patient | undefined>;
  rules: PatientDetailsInsuranceRules;
  validationLists: PatientDetailsValidationLists;
}): CurrentPatientValidationState => {
  const currentPatient = patients[currentPatientIndex];
  const currentPatientShowsInsuranceForms =
    !!currentPatient &&
    shouldShowInsuranceForms({
      patient: currentPatient,
      index: currentPatientIndex,
      insurancePlan: validationLists.insurancePlan,
      insuranceDrivesAvailability: rules.insuranceDrivesAvailability,
    });

  return {
    currentSecondaryInsuranceFormVisible:
      !!currentPatient &&
      currentPatientShowsInsuranceForms &&
      rules.showSecondaryInsurancePlanQuestion &&
      shouldShowSecondaryInsuranceForm({
        patient: currentPatient,
        index: currentPatientIndex,
        secondaryInsuranceAnswer: validationLists.secondaryInsuranceAnswer,
      }),
  };
};
export const getIncompletePatientNames = ({
  currentPatientIndex,
  fallbackPatientName,
  patients,
  rules,
  validationLists,
}: {
  currentPatientIndex: number;
  fallbackPatientName: string;
  patients: Array<Patient | undefined>;
  rules: PatientDetailsInsuranceRules;
  validationLists: PatientDetailsValidationLists;
}) =>
  patients.reduce<string[]>((acc, patient, index) => {
    const patientDetailsIncomplete =
      validationLists.listEnableButton[index] === false;
    const insuranceIncomplete = patient
      ? isPatientInsuranceIncomplete({
          patient,
          index,
          rules,
          validationLists,
        })
      : false;

    if (
      patient &&
      index !== currentPatientIndex &&
      (patientDetailsIncomplete || insuranceIncomplete)
    ) {
      acc.push(getPatientDisplayName(patient, fallbackPatientName));
    }

    return acc;
  }, []);

export const shouldShowPrimaryInsurancePlanError = ({
  currentPatientIndex,
  index,
  patient,
  submitAttemptCount,
  rules,
  validationLists,
}: {
  currentPatientIndex: number;
  index: number;
  patient: Patient;
  submitAttemptCount: number;
  rules: PatientDetailsInsuranceRules;
  validationLists: PatientDetailsValidationLists;
}) =>
  submitAttemptCount > 0 &&
  currentPatientIndex === index &&
  shouldShowInsuranceSection({ patient, rules }) &&
  rules.showPrimaryInsurancePlanQuestion &&
  rules.primaryInsurancePlanRequired &&
  !(validationLists.insurancePlan[index] || patient.insurance?.plan);

export const shouldShowSecondaryInsurancePlanError = ({
  currentPatientIndex,
  index,
  patient,
  submitAttemptCount,
  rules,
  validationLists,
}: {
  currentPatientIndex: number;
  index: number;
  patient: Patient;
  submitAttemptCount: number;
  rules: PatientDetailsInsuranceRules;
  validationLists: PatientDetailsValidationLists;
}) =>
  submitAttemptCount > 0 &&
  currentPatientIndex === index &&
  shouldShowInsuranceForms({
    patient,
    index,
    insurancePlan: validationLists.insurancePlan,
    insuranceDrivesAvailability: rules.insuranceDrivesAvailability,
  }) &&
  rules.showSecondaryInsurancePlanQuestion &&
  rules.secondaryInsurancePlanRequired &&
  !(
    validationLists.secondaryInsuranceAnswer[index] ||
    getSecondaryInsurancePlanAnswer(patient)
  );

export const buildPatientDetailsFormDataOnUnmount = ({
  formData,
  patient,
  insurancePlan,
  insuranceDrivesAvailability,
  secondaryInsuranceAnswer,
  showSecondaryInsurancePlanQuestion,
}: {
  formData?: NestedObject;
  patient: Patient;
  insurancePlan?: string;
  insuranceDrivesAvailability: boolean;
  secondaryInsuranceAnswer?: string;
  showSecondaryInsurancePlanQuestion: boolean;
}) => {
  const plan = insuranceDrivesAvailability
    ? patient.insurance?.plan
    : insurancePlan || patient.insurance?.plan;
  const shouldPersistSecondaryInsuranceAnswer =
    plan === StaticValues.YES && showSecondaryInsurancePlanQuestion;
  const resolvedSecondaryInsuranceAnswer = shouldPersistSecondaryInsuranceAnswer
    ? secondaryInsuranceAnswer || getSecondaryInsurancePlanAnswer(patient)
    : '';
  const shouldClearSecondaryInsurance =
    resolvedSecondaryInsuranceAnswer === StaticValues.NO;
  const shouldPersistInsurancePlan = !!plan && !insuranceDrivesAvailability;
  const insuranceFormData =
    plan === StaticValues.NO
      ? {
          plan,
          id: '',
          name: '',
          other: '',
          memberId: '',
        }
      : { plan };

  return shouldPersistInsurancePlan || shouldClearSecondaryInsurance
    ? {
        ...formData,
        patientDetails: {
          ...(formData?.patientDetails as object),
          ...(shouldClearSecondaryInsurance
            ? {
                secondaryInsuranceDetails: undefined,
              }
            : {}),
        },
        ...(shouldPersistInsurancePlan
          ? {
              insurance: insuranceFormData,
            }
          : {}),
      }
    : formData;
};

export const buildInitialPatientDetailsEnableList = (
  patients: Array<Patient | undefined>,
): Array<boolean | undefined> =>
  patients.map(patient => (patient ? false : undefined));

export const updateIndexedBooleanList = ({
  currentList,
  index,
  value,
}: {
  currentList: boolean[];
  index: number;
  value: boolean;
}) => {
  const nextList = [...currentList];
  nextList[index] = value;
  return nextList;
};

export const updatePatientDetailsEnableList = ({
  currentList,
  index,
  value,
}: {
  currentList: Array<boolean | undefined>;
  index: number;
  value: boolean;
}) => {
  if (currentList[index] === undefined || currentList[index] === value) {
    return currentList;
  }

  const nextList = [...currentList];
  nextList[index] = value;
  return nextList;
};

export const buildPatientDetailsFormIdsList = ({
  patients,
  showCustomFieldsPatient,
}: {
  patients: Array<Patient | undefined>;
  showCustomFieldsPatient?: boolean;
}) =>
  patients.map(() => {
    const formIds = [FormIds.PatientDetail];

    if (showCustomFieldsPatient) {
      formIds.push(FormIds.CustomFieldsPatient);
    }

    return formIds;
  });

export interface PrefilledPatientDetailsSource {
  dateOfBirth?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: {
    number?: string | number;
  };
}

export const buildPatientDetailsFormOptionsList = ({
  countryList,
  countryStatesList,
  genderList,
  infoUser,
  patients,
  readOnlyPrefilledPatientsDetailsFields,
  showCustomFieldsPatient,
}: {
  countryList: FormBuilderObOptions['countryList'];
  countryStatesList: FormBuilderObOptions['countryStatesList'];
  genderList: FormBuilderObOptions['genderList'];
  infoUser?: PrefilledPatientDetailsSource;
  patients: Array<Patient | undefined>;
  readOnlyPrefilledPatientsDetailsFields: boolean;
  showCustomFieldsPatient?: boolean;
}): Array<Array<FormBuilderObOptions>> =>
  patients.map((_, index) => {
    const formOptions: Array<FormBuilderObOptions> = [
      {
        countryList,
        countryStatesList,
        genderList,
        readOnlyPrefilledPatientsDetailsFields,
        prefilledPatientDetailsFields: {
          firstName: index === 0 && !!infoUser?.firstName,
          lastName: index === 0 && !!infoUser?.lastName,
          dateOfBirth: index === 0 && !!infoUser?.dateOfBirth,
          email: index === 0 && !!infoUser?.email,
          phoneNumber: index === 0 && !!infoUser?.phoneNumber?.number,
        },
      },
    ];

    if (showCustomFieldsPatient) {
      formOptions.push({});
    }

    return formOptions;
  });

export const hasAnyLoading = (...loadingStates: boolean[]) =>
  loadingStates.some(Boolean);

export const hasAnyError = (...errorStates: boolean[]) =>
  errorStates.some(Boolean);
